import { useState, useEffect } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120),
  email: z.string().trim().email("Valid email required").max(255),
  phone: z.string().trim().max(40).optional(),
  category: z.string(),
  priority: z.string(),
  subject: z.string().trim().min(3, "Add a short subject").max(200),
  description: z.string().trim().min(5, "Describe the issue").max(5000),
});

const CATEGORIES = [
  { v: "technical", l: "Technical issue" },
  { v: "maintenance", l: "Maintenance" },
  { v: "mapping", l: "Mapping / setup" },
  { v: "deployment", l: "Deployment" },
  { v: "training", l: "Training" },
  { v: "warranty", l: "Warranty / repair" },
  { v: "upgrade", l: "Upgrade / accessory" },
  { v: "robot", l: "Robot question" },
  { v: "billing", l: "Billing" },
  { v: "other", l: "Other" },
];

interface Props {
  defaultCategory?: string;
  onSubmitted?: (ticketNumber: number) => void;
}

const PublicTicketForm = ({ defaultCategory = "other", onSubmitted }: Props) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    category: defaultCategory,
    priority: "normal",
    subject: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<null | number>(null);

  useEffect(() => {
    if (user?.email && !form.email) {
      setForm((f) => ({ ...f, email: user.email!, name: f.name || (user.user_metadata?.full_name as string) || "" }));
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message || "Please check the form");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-support-ticket", {
        body: parsed.data,
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      const ticketNumber = (data as any).ticket_number as number;
      setSuccess(ticketNumber);
      toast.success(`Ticket #${ticketNumber} created. Check your email for confirmation.`);
      onSubmitted?.(ticketNumber);
    } catch (err: any) {
      toast.error(err?.message || "Could not submit ticket. Call (336) 203-3184.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-10 px-4">
        <CheckCircle2 className="w-14 h-14 text-[#00F5A0] mx-auto mb-4" />
        <h3 className="text-2xl font-bold mb-2">Ticket #{success} created</h3>
        <p className="text-muted-foreground mb-6">
          We sent a confirmation to <span className="text-primary">{form.email}</span>. Our team responds within 1 business hour.
        </p>
        <p className="text-sm text-muted-foreground">
          Need urgent help? Call <a className="text-primary hover:underline" href="tel:+13362033184">(336) 203-3184</a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="t-name">Your name *</Label>
          <Input id="t-name" required value={form.name} onChange={set("name")} className="bg-[#05070A] border-primary/30" />
        </div>
        <div>
          <Label htmlFor="t-email">Email *</Label>
          <Input id="t-email" type="email" required value={form.email} onChange={set("email")} className="bg-[#05070A] border-primary/30" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="t-phone">Phone (optional)</Label>
          <Input id="t-phone" value={form.phone} onChange={set("phone")} className="bg-[#05070A] border-primary/30" />
        </div>
        <div>
          <Label>Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="bg-[#05070A] border-primary/30"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="t-subject">Subject *</Label>
        <Input id="t-subject" required value={form.subject} onChange={set("subject")} placeholder="e.g. CC1 Pro won't connect to WiFi" className="bg-[#05070A] border-primary/30" />
      </div>
      <div>
        <Label htmlFor="t-desc">What's happening? *</Label>
        <Textarea
          id="t-desc"
          required
          value={form.description}
          onChange={set("description")}
          rows={6}
          placeholder="Describe what's happening, what you've tried, and any error messages."
          className="bg-[#05070A] border-primary/30"
        />
      </div>
      <div>
        <Label>Priority</Label>
        <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
          <SelectTrigger className="bg-[#05070A] border-primary/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low — general question</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="high">High — affecting operations</SelectItem>
            <SelectItem value="urgent">Urgent — safety or downtime</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={loading} size="lg" className="w-full bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-bold h-12">
        {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
        {loading ? "Submitting…" : "Submit Ticket"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        No account required. By submitting you consent to be contacted about this request.
      </p>
    </form>
  );
};

export default PublicTicketForm;
